import * as preact from "preact";
import PropTypes from "prop-types";
import Portal from "../Portal";
import { stopPropagation } from "../../foundation/utils/legacy";
import noderef from "../../foundation/utils/noderef";
import classname from "../../foundation/utils/classname";
import dialogPolyfill from "dialog-polyfill";

import DialogFooter from "./DialogFooter";

import "./style.css";

const { h } = preact;

/**
 * A generic Dialog component that contains positiveButton, negativeButton and dom content
 *
 * @class Dialog
 * @extends {preact.Component}
 */
class Dialog extends preact.Component {
    constructor(props) {
        super(props);

        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    handleKeyDown(ev) {
        ev.stopPropagation();
    }

    componentDidMount() {
        document.addEventListener("keydown", this.handleKeyDown, true);
        dialogPolyfill.registerDialog(this.nodeRefs.dialog);

        this.nodeRefs.dialog.showModal();
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown, true);
    }

    render() {
        const {
            positiveButtonText,
            negativeButtonText,
            fallbackButtonText,
            positiveButtonType,
            negativeButtonType,
            fallbackButtonType,
            positiveButtonDisabled,
            negativeButtonDisabled,
            fallbackButtonDisabled,
            onPositiveButtonClick,
            onNegativeButtonClick,
            onFallbackButtonClick,
            hidePositiveButton,
            hideNegativeButton,
            onClose,
            width,
            footer,
            loading,
            loadingText,
            class: cssClass,
            bodyClass: bodyCssClass
        } = this.props;

        return (
            <Portal>
                <dialog
                    data-testid={this.props["data-testid"]}
                    onClick={stopPropagation}
                    onClose={onClose}
                    style={{ width }}
                    class={classname(cssClass, "dialog", { loading })}
                    ref={noderef(this, "dialog")}>
                    <div class={classname(bodyCssClass, "body")}>
                        {this.props.children}
                    </div>
                    <DialogFooter
                        positiveButtonText={positiveButtonText}
                        negativeButtonText={negativeButtonText}
                        fallbackButtonText={fallbackButtonText}
                        positiveButtonType={positiveButtonType}
                        negativeButtonType={negativeButtonType}
                        fallbackButtonType={fallbackButtonType}
                        positiveButtonDisabled={positiveButtonDisabled}
                        negativeButtonDisabled={negativeButtonDisabled}
                        fallbackButtonDisabled={fallbackButtonDisabled}
                        onPositiveButtonClick={onPositiveButtonClick}
                        onNegativeButtonClick={onNegativeButtonClick || onClose}
                        onFallbackButtonClick={onFallbackButtonClick}
                        hidePositiveButton={hidePositiveButton}
                        hideNegativeButton={hideNegativeButton}
                        footer={footer}
                        loading={loading}
                        loadingText={loadingText}/>
                </dialog>
            </Portal>
        );
    }
}

Dialog.defaultProps = {
    positiveButtonText: "DONE",
    negativeButtonText: "CANCEL",
    hidePositiveButton: false,
    hideNegativeButton: true,
    positiveButtonType: "submit",
    negativeButtonType: "submit",
    fallbackButtonType: "submit"
};

Dialog.propTypes = {
    positiveButtonText: PropTypes.string.isRequired,
    negativeButtonText: PropTypes.string,
    fallbackButtonText: PropTypes.string,
    positiveButtonType: PropTypes.string.isRequired,
    negativeButtonType: PropTypes.string.isRequired,
    fallbackButtonType: PropTypes.string.isRequired,
    positiveButtonDisabled: PropTypes.bool,
    negativeButtonDisabled: PropTypes.bool,
    onPositiveButtonClick: PropTypes.func,
    onNegativeButtonClick: PropTypes.func,
    onFallbackButtonClick: PropTypes.func,
    hidePositiveButton: PropTypes.bool,
    hideNegativeButton: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onClose: PropTypes.func,
    children: PropTypes.node,
    footer: PropTypes.node,
    loading: PropTypes.bool,
    loadingText: PropTypes.string
};

export default Dialog;
